// Emotes (Design §11.4, Wunsch User 2026-10-26): a speech bubble over the own tank for 2 s,
// online only. Keys 1–4; controller Y/△ picks, X/□ sends; the touch controller has 💬. The free
// ones everyone has; packs from the cosmetics shop, the keys chosen there.
import { el } from './dom.js';

type Face = 'happy' | 'wow' | 'sad' | 'angry';
type Anim = 'heart' | 'thumb' | 'question' | 'sweat';

/** One emote: shown as `text` (with an emoji `symbol`), a drawn `face` or a drawn `anim`; `label` in the shop. */
interface Emote {
  label: string;
  text?: string;
  symbol?: string;
  face?: Face;
  anim?: Anim;
}

const say = (text: string): Emote => ({ label: text, text });

/**
 * By emote id (server `looks::EMOTE_COUNT`, never renumber): the free ones, then four per pack
 * in pack order (`EMOTE_PACKS`).
 */
export const EMOTES: Emote[] = [
  say('GG'),
  say('Oops'),
  say('Hi'),
  say('Thanks'),
  // Polite
  say('Well played'),
  say('Sorry'),
  say('Again?'),
  say('Good luck'),
  // Cheeky
  say('Too slow'),
  say('Missed!'),
  say('Come here'),
  say('Hehe'),
  // Faces
  { label: 'Laughing face', face: 'happy' },
  { label: 'Amazed face', face: 'wow' },
  { label: 'Sad face', face: 'sad' },
  { label: 'Angry face', face: 'angry' },
  // Tactics
  say('Help!'),
  say('Behind you'),
  say('Power-up here'),
  say('Zone!'),
  // Animals
  { label: 'Meow!', text: 'Meow!', symbol: '🐱' },
  { label: 'Woof!', text: 'Woof!', symbol: '🐶' },
  { label: 'Cluck!', text: 'Cluck!', symbol: '🐔' },
  { label: 'Ribbit!', text: 'Ribbit!', symbol: '🐸' },
  // Animated
  { label: 'Beating heart', anim: 'heart' },
  { label: 'Thumbs up', anim: 'thumb' },
  { label: 'Hopping question mark', anim: 'question' },
  { label: 'Drop of sweat', anim: 'sweat' },
];

/** The free emotes (`protocol::FREE_EMOTES`), the standard on the keys 1–4. */
export const FREE_EMOTES = 4;
export const STANDARD_KEYS = [0, 1, 2, 3];
/** Emote packs: their catalog item ids, in order; pack `k` holds emotes `FREE_EMOTES + 4k ..`. */
export const EMOTE_PACKS = [281, 282, 283, 284, 285, 286];

/** The emotes of pack item `item`. */
export function packEmotes(item: number): number[] {
  const k = EMOTE_PACKS.indexOf(item);
  return k < 0 ? [] : [0, 1, 2, 3].map((n) => FREE_EMOTES + 4 * k + n);
}

/** The emotes `owned` (catalog ids) gives: the free ones and those of the packs bought. */
export function ownedEmotes(owned: number[]): number[] {
  return [...STANDARD_KEYS, ...EMOTE_PACKS.filter((p) => owned.includes(p)).flatMap(packEmotes)];
}

const HIDE_KEY = 'webtanks.hideEmotes';
let hideOthers = localStorage.getItem(HIDE_KEY) === '1';

/** Whether to show tank `tank`'s emotes: one's own always, the others' unless hidden. */
export function showsEmotesOf(tank: number, you: number): boolean {
  return tank === you || !hideOthers;
}

/** Status bar button: show or hide the others' emotes (kept in the browser). */
export function emoteButton(): HTMLElement {
  const button = el('button', { className: 'sound' });
  const show = () => {
    button.textContent = hideOthers ? '🗨️' : '💬';
    button.style.opacity = hideOthers ? '0.45' : '1';
    button.title = hideOthers ? "Others' emotes hidden · click: show them" : "Others' emotes shown · click: hide them";
  };
  button.onclick = () => {
    hideOthers = !hideOthers;
    localStorage.setItem(HIDE_KEY, hideOthers ? '1' : '0');
    show();
    button.blur(); // keys go on steering the tank
  };
  show();
  return button;
}

/** How long a bubble takes to pop up and to fade, as a share of its 2 s. */
const POP = 0.06;
const FADE = 0.85;
const EMOJI_FONT = '"Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", system-ui, sans-serif';
const INK = '#222';

/**
 * Bubble with emote `id` over a tank at `x`, `y` (radius `r`); `age` 0..1 of its time, `now` for
 * the animated ones; `preview`: dashed and faint (the controller's pick, not sent yet).
 */
export function drawBubble(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, id: number, age: number, now: number, preview = false): void {
  const e = EMOTES[id] ?? say('…');
  const grow = Math.min(1, age / POP);
  const alpha = preview ? 0.6 : age > FADE ? (1 - age) / (1 - FADE) : 1;
  ctx.save();
  ctx.globalAlpha *= alpha;
  ctx.font = 'bold 13px system-ui, sans-serif';
  const pictured = !!(e.face || e.anim);
  const symbol = e.symbol ? 18 : 0;
  const w = pictured ? 30 : ctx.measureText(e.text ?? '').width + 14 + symbol;
  const h = pictured ? 26 : 20;
  ctx.translate(x, y - r - 30 - h / 2);
  ctx.scale(0.6 + 0.4 * grow, 0.6 + 0.4 * grow);
  ctx.beginPath();
  ctx.roundRect(-w / 2, -h / 2, w, h, 8);
  ctx.moveTo(-5, h / 2);
  ctx.lineTo(0, h / 2 + 7);
  ctx.lineTo(5, h / 2);
  ctx.fillStyle = '#fffdf4';
  ctx.fill();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = INK;
  if (preview) ctx.setLineDash([4, 3]);
  ctx.stroke();
  ctx.setLineDash([]);
  if (e.face) drawFace(ctx, e.face);
  else if (e.anim) drawAnim(ctx, e.anim, now);
  else {
    ctx.fillStyle = INK;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(e.text ?? '', symbol / 2, 0.5);
    if (e.symbol) {
      ctx.font = `14px ${EMOJI_FONT}`;
      ctx.fillText(e.symbol, -w / 2 + 12, 1);
    }
  }
  ctx.restore();
}

/** A round yellow face, 9 across, with the mood in eyes, brows and mouth. */
function drawFace(ctx: CanvasRenderingContext2D, face: Face): void {
  const r = 9;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fillStyle = face === 'angry' ? '#f07a4a' : '#ffd54a';
  ctx.fill();
  ctx.lineWidth = 1.2;
  ctx.strokeStyle = INK;
  ctx.stroke();
  ctx.fillStyle = INK;
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.ellipse(side * 3.2, -2.2, 1.2, face === 'wow' ? 1.9 : 1.4, 0, 0, Math.PI * 2);
    ctx.fill();
    if (face === 'angry' || face === 'sad') {
      // Brows: down to the middle when angry, up when sad.
      const tilt = face === 'angry' ? 1 : -1;
      ctx.beginPath();
      ctx.moveTo(side * 5, -5.2 - tilt);
      ctx.lineTo(side * 1.6, -5.2 + tilt);
      ctx.stroke();
    }
  }
  ctx.beginPath();
  if (face === 'happy') {
    ctx.arc(0, 1.5, 4.6, 0.1 * Math.PI, 0.9 * Math.PI);
    ctx.closePath();
    ctx.fillStyle = '#8b2a1e';
    ctx.fill();
    ctx.stroke();
  } else if (face === 'wow') {
    ctx.ellipse(0, 4, 2, 2.6, 0, 0, Math.PI * 2);
    ctx.fillStyle = '#8b2a1e';
    ctx.fill();
  } else {
    // A frown: deep when sad, tight when angry.
    ctx.arc(0, face === 'sad' ? 7.5 : 8, face === 'sad' ? 4 : 3.5, 1.2 * Math.PI, 1.8 * Math.PI);
    ctx.stroke();
    if (face === 'sad') {
      ctx.beginPath();
      ctx.ellipse(4.6, 1.5, 1, 1.6, 0, 0, Math.PI * 2); // a tear
      ctx.fillStyle = '#4aa8e8';
      ctx.fill();
    }
  }
}

/** The animated ones: a beating heart, a wagging thumb, a hopping question mark, a running drop. */
function drawAnim(ctx: CanvasRenderingContext2D, anim: Anim, now: number): void {
  const t = now / 1000;
  ctx.save();
  if (anim === 'heart') {
    const s = 1 + 0.18 * Math.max(0, Math.sin(t * Math.PI * 4));
    ctx.scale(s, s);
    ctx.beginPath();
    ctx.moveTo(0, 7);
    ctx.bezierCurveTo(-11, -1, -6, -10, 0, -4);
    ctx.bezierCurveTo(6, -10, 11, -1, 0, 7);
    ctx.fillStyle = '#e8364a';
    ctx.fill();
  } else if (anim === 'thumb') {
    ctx.rotate(0.35 * Math.sin(t * Math.PI * 3));
    ctx.font = `16px ${EMOJI_FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('👍', 0, 1);
  } else if (anim === 'question') {
    const hop = -Math.abs(Math.sin(t * Math.PI * 2.5)) * 4;
    ctx.font = 'bold 18px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#2a7fd0';
    ctx.fillText('?', 0, 2 + hop);
  } else {
    // A drop runs down a little forehead line and starts again.
    const run = (t * 0.8) % 1;
    ctx.beginPath();
    ctx.moveTo(-9, -6);
    ctx.lineTo(9, -6);
    ctx.strokeStyle = '#999';
    ctx.lineWidth = 1;
    ctx.stroke();
    const dy = -4 + run * 10;
    ctx.globalAlpha *= 1 - run * 0.6;
    ctx.beginPath();
    ctx.moveTo(2, dy - 5);
    ctx.quadraticCurveTo(6, dy + 1, 2, dy + 2.5);
    ctx.quadraticCurveTo(-2, dy + 1, 2, dy - 5);
    ctx.fillStyle = '#4aa8e8';
    ctx.fill();
  }
  ctx.restore();
}
